import { type SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import classNames from 'classnames'
import { BreakoutTooltip } from '../design-system/BreakoutTooltip'
import { observer } from 'mobx-react-lite'
import { SlideDeckMaterialType } from '@breakoutlearning/firebase-repository/types'
import { inMobileSafari } from 'config/browser'
import { LinkIcon } from '../icons/Link'
import { DownloadIcon } from '../icons/Download'
import { Podcasts } from '../icons/Podcasts'
import { EyeOpen } from '../icons/EyeOpen'
import { useDialogs } from 'hooks/dialogs'
import { LazyMaterialPdfDialog } from './LazyMaterialPdfDialog'
import { MaterialPlayerDialog } from './MaterialPlayerDialog'
import { useBreakoutUser } from 'hooks/profile'
import { Spinner } from '../Spinner'
import { useState } from 'react'
import { VideoPreviewDialog } from 'components/VideoPreviewDialog'
import { ImagePreviewDialog } from './ImagePreviewDialog'
import { formatFilename } from 'util/strings'
import { useTranslationTyped } from 'i18n/i18n'

type DivProps = React.HTMLAttributes<HTMLDivElement>

export function CourseMaterialListItem({
  material,
  slideDeckId,
  slideDeckName,
  onViewTap = () => {},
  onPdfDownloadTap = () => {},
  slideDeckVersion,
  className,
  inlineDialogs = false,
  ...rest
}: DivProps & {
  slideDeckName: string
  material: SlideDeckMaterial
  slideDeckId: string
  slideDeckVersion: string
  onViewTap?: () => void
  onPdfDownloadTap?: () => void
  inlineDialogs?: boolean
}) {
  return (
    <div
      className={classNames(
        'flex flex-row items-center justify-between text-title-small',
        className
      )}
      {...rest}
    >
      <BreakoutTooltip content={material.data.materialName}>
        <span className="mr-3 line-clamp-2">{material.data.materialName}</span>
      </BreakoutTooltip>
      <CourseMaterialButton
        slideDeckVersion={slideDeckVersion}
        onViewTap={onViewTap}
        onPdfDownloadTap={onPdfDownloadTap}
        material={material}
        slideDeckId={slideDeckId}
        slideDeckName={slideDeckName}
        inlineDialogs={inlineDialogs}
      />
    </div>
  )
}

export const CourseMaterialButton = observer(function CourseMaterialButton({
  material,
  slideDeckId,
  slideDeckName,
  onViewTap = () => {},
  onPdfDownloadTap = () => {},
  slideDeckVersion,
  omitPdfDownload = false,
  size = 20,
  inlineDialogs = false,
}: {
  material: SlideDeckMaterial
  slideDeckVersion: string
  slideDeckId: string
  slideDeckName: string
  onViewTap?: () => void
  onPdfDownloadTap?: () => void
  omitPdfDownload?: boolean
  size?: number
  inlineDialogs?: boolean
}) {
  const { showDialog } = useDialogs()
  const user = useBreakoutUser()
  const { tt } = useTranslationTyped()

  const [pdfDownloading, setPDFDownloading] = useState(false)

  if (!material.data.materialLink && !material.data.imageUrl) return null

  // exhaustive switch on materialType return button(s) for each type
  switch (material.data.materialType) {
    case SlideDeckMaterialType.url:
      return (
        <button
          aria-label={`View ${material.data.materialName}`}
          onClick={() => {
            onViewTap()
            window.open(material.data.materialLink, '_blank')
          }}
        >
          <LinkIcon size={size} className="min-w-5 cursor-pointer" />
        </button>
      )
    case SlideDeckMaterialType.pdf: {
      const viewButton = !inMobileSafari ? (
        <button
          className="p-1"
          aria-label={tt.student_assignment.prework_accessibility_label({
            materialName: material.data.materialName,
          })}
          id={`course-material-button-${material.id}`}
          onClick={() => {
            onViewTap()
            showDialog(() => (
              <LazyMaterialPdfDialog
                src={material.data.materialLink}
                inline={inlineDialogs}
              />
            ))
          }}
        >
          <EyeOpen size={size} className="min-w-5 cursor-pointer" />
        </button>
      ) : null

      if (omitPdfDownload) return viewButton

      return (
        <div className="flex flex-row gap-2">
          {viewButton}
          {!pdfDownloading ? (
            <button
              className="p-1"
              aria-label={`Download ${material.data.materialName}`}
              onClick={async () => {
                // Ex. airbnb_summary_v1_0_1.pdf
                const fileName = formatFilename({
                  nameParts: [
                    slideDeckName,
                    material.data.materialName,
                    'v' + slideDeckVersion,
                  ],
                  extension: 'pdf',
                })

                try {
                  setPDFDownloading(true)
                  onPdfDownloadTap()
                  const token =
                    (await user.repository.currentUser?.getIdToken()) || ''
                  await downloadPdf(material.data.materialLink, fileName, token)
                } finally {
                  setPDFDownloading(false)
                }
              }}
            >
              <DownloadIcon size={size} className="min-w-5 cursor-pointer" />
            </button>
          ) : (
            <Spinner size={1.25} />
          )}
        </div>
      )
    }
    case SlideDeckMaterialType.mp3:
      return (
        <button
          className="p-1"
          id={`course-material-button-${material.id}`}
          aria-label={`Listen to ${material.data.materialName}`}
          onClick={() => {
            onViewTap()
            showDialog(() => (
              <MaterialPlayerDialog
                slideDeckId={slideDeckId}
                slideDeckName={slideDeckName}
                inline={inlineDialogs}
                materialData={material.data}
              />
            ))
          }}
        >
          <Podcasts size={size} className="min-w-5 cursor-pointer" />
        </button>
      )
    case SlideDeckMaterialType.mp4:
    case SlideDeckMaterialType.webm:
      return (
        <button
          className="p-1"
          aria-label={`View ${material.data.materialName}`}
          id={`course-material-button-${material.id}`}
          onClick={() => {
            onViewTap()
            showDialog(() => (
              <VideoPreviewDialog
                src={material.data.materialLink}
                inline={inlineDialogs}
                requiresAuthHeaders={true}
                alt={material.data.materialName}
              />
            ))
          }}
        >
          <EyeOpen size={size} className="min-w-5 cursor-pointer" />
        </button>
      )
    case SlideDeckMaterialType.featuredLarge:
    case SlideDeckMaterialType.img:
      return (
        <button
          className="p-1"
          aria-label={`View ${material.data.materialName}`}
          id={`course-material-button-${material.id}`}
          onClick={() => {
            onViewTap()
            showDialog(() => (
              <ImagePreviewDialog
                src={material.data.materialLink}
                inline={inlineDialogs}
                alt={material.data.materialName}
              />
            ))
          }}
        >
          <EyeOpen size={size} className="min-w-5 cursor-pointer" />
        </button>
      )
  }
})

const downloadPdf = async (url: string, fileName: string, token: string) => {
  // Signed URLs include auth in the signature — adding an Authorization
  // header causes CORS errors because the preflight changes.
  const headers: HeadersInit = url.includes('X-Goog-Signature=')
    ? {}
    : { Authorization: `Bearer ${token}` }
  const response = await fetch(url, {
    method: 'GET',
    headers,
  })
  if (!response.ok) {
    throw new Error('Failed to fetch PDF')
  }
  const blob = await response.blob()
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(blobUrl)
}
